import {
  Divider as MuiDivider,
  DividerProps as MuiDividerProps,
} from '@mui/material';
import clsx from 'clsx';

import { mergeSx, negate } from '../utils';
import { dividerVars, insetVars } from './variables';
import { classNamesFor } from '../utils/classNames';

const classNamesFactory = classNamesFor('Divider');

const classNames = {
  root: classNamesFactory.root(),
  horizontal: classNamesFactory.attribute('horizontal'),
  vertical: classNamesFactory.attribute('vertical'),
  bleed: classNamesFactory.attribute('bleed'),
  inset: classNamesFactory.attribute('inset'),
};

export type DividerProps = {
  /**
   * Divider color is controlled via `--divider-color` CSS variable. This property overrides it.
   */
  color?: string;
  /**
   * Should the divider be smaller than the container?
   * If true, it will apply the horizontal margins to the divider, as provided via `--inset-pl/--inset-pr` CSS variables.
   * For vertical dividers, it will apply the top and bottom margins, as provided via `--inset-pt/--inset-pb` CSS variables.
   */
  inset?: boolean;
  /**
   * Should the divider bleed out of its container's inset?
   * If true, it will apply negative horizontal margins, canceling the `--inset-pl/--inset-pr` CSS variables.
   * For vertical dividers, it will apply negative top and bottom margins, canceling `--inset-pt/--inset-pb` CSS variables.
   */
  bleed?: boolean;
  className?: string;
} & Pick<MuiDividerProps, 'sx' | 'orientation' | 'flexItem'>;

export const Divider = ({
  color,
  sx,
  inset,
  bleed,
  orientation,
  className,
  ...rest
}: DividerProps) => {
  const isVertical = orientation === 'vertical';

  return (
    <MuiDivider
      orientation={orientation}
      sx={mergeSx(
        {
          borderColor: color ?? dividerVars.refs.color,
          ...(inset &&
            (isVertical
              ? {
                  marginTop: insetVars.refs.pt,
                  marginBottom: insetVars.refs.pb,
                }
              : {
                  marginLeft: insetVars.refs.pl,
                  marginRight: insetVars.refs.pr,
                })),
          ...(bleed &&
            (isVertical
              ? {
                  marginTop: negate(insetVars.refs.pt),
                  marginBottom: negate(insetVars.refs.pb),
                }
              : {
                  marginLeft: negate(insetVars.refs.pl),
                  marginRight: negate(insetVars.refs.pr),
                })),
        },
        sx
      )}
      className={clsx(classNames.root, className, {
        [classNames.horizontal]: orientation === 'horizontal',
        [classNames.vertical]: orientation === 'vertical',
        [classNames.inset]: inset,
        [classNames.bleed]: bleed,
      })}
      {...rest}
    />
  );
};

Divider.classNames = classNames;
